import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';


// ─── helpers ────────────────────────────────────────────────────────────────

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

// ─── sub-components ───────────────────────────────────────────────────────────

function Spinner({ size = 40, color = '#f97316' }) {
  return (
    <>
      <div style={{
        width: size,
        height: size,
        border: `4px solid rgba(0,0,0,0.08)`,
        borderTop: `4px solid ${color}`,
        borderRadius: '50%',
        animation: 'hl-spin 0.9s linear infinite',
        margin: '0 auto',
      }} />
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

// Small inline spinner used inside buttons
function ButtonSpinner() {
  return (
    <span style={{
      display: 'inline-block',
      width: 16,
      height: 16,
      border: '2px solid rgba(255,255,255,0.4)',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'hl-spin 0.9s linear infinite',
      verticalAlign: 'middle',
      marginRight: 8,
    }} />
  );
}

// ─── field row (view mode) ────────────────────────────────────────────────────

function InfoRow({ label, value }) {
  return (
    <div style={{
      padding: '14px 0',
      borderBottom: '1px solid #f3f4f6',
    }}>
      <div style={{
        fontSize: 13,
        color: '#9ca3af',
        marginBottom: 4,
        fontWeight: 500,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16,
        color: value ? '#111827' : '#d1d5db',
        fontWeight: value ? 500 : 400,
        wordBreak: 'break-word',
      }}>
        {value || '—'}
      </div>
    </div>
  );
}

// ─── styled text input ────────────────────────────────────────────────────────

function TextInput({ label, value, onChange, placeholder, type = 'text' }) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block',
        fontSize: 13,
        color: '#6b7280',
        fontWeight: 500,
        marginBottom: 6,
      }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          display: 'block',
          width: '100%',
          boxSizing: 'border-box',
          padding: '11px 14px',
          fontSize: 15,
          color: '#111827',
          background: '#ffffff',
          border: `1.5px solid ${focused ? '#f97316' : '#e5e7eb'}`,
          borderRadius: 10,
          outline: 'none',
          transition: 'border-color 0.15s ease',
          WebkitAppearance: 'none',
        }}
      />
    </div>
  );
}

// ─── toast banner ─────────────────────────────────────────────────────────────

function Toast({ message, type = 'success', visible }) {
  if (!visible) return null;
  const bg = type === 'success' ? '#10b981' : '#ef4444';
  return (
    <div style={{
      position: 'fixed',
      top: 76,
      left: '50%',
      transform: 'translateX(-50%)',
      background: bg,
      color: 'white',
      fontSize: 14,
      fontWeight: 600,
      padding: '10px 22px',
      borderRadius: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      zIndex: 9999,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    }}>
      {message}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

const VenueDashboardInfoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // page state machine
  const [pageState, setPageState] = useState('loading'); // loading | ready | error-auth | error-generic

  // venue data
  const [venue, setVenue] = useState(null);
  const [myRole, setMyRole] = useState(null);

  // edit mode
  const [editMode, setEditMode] = useState(false);
  const [editPhone, setEditPhone] = useState('');
  const [editWebsite, setEditWebsite] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editLandmarkHint, setEditLandmarkHint] = useState('');

  // save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // toast
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimerRef = useRef(null);

  // ── fetch ───────────────────────────────────────────────────────────────────

  const fetchVenueData = useCallback(async () => {
    setPageState('loading');
    setSaveError('');

    const token = getAuthToken();
    if (!token) {
      setPageState('error-auth');
      return;
    }

    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        setPageState('error-auth');
        return;
      }

      if (!res.ok) {
        setPageState('error-generic');
        return;
      }

      const data = await res.json();

      if (!data.success) {
        setPageState('error-generic');
        return;
      }

      const v = data.venue || {};
      setVenue(v);
      setMyRole(data.myRole || null);

      // Pre-seed edit fields from current values
      setEditPhone(v.phone || '');
      setEditWebsite(v.website || '');
      setEditAddress(v.address || v.street || '');
      setEditLandmarkHint(v.landmarkHint || '');

      setPageState('ready');
    } catch (err) {
      console.error('[VenueDashboardInfoPage] fetch error:', err);
      setPageState('error-generic');
    }
  }, [slug]);

  useEffect(() => {
    fetchVenueData();
  }, [fetchVenueData]);

  // cleanup toast timer on unmount
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // ── show toast ──────────────────────────────────────────────────────────────

  const showToast = (msg) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2000);
  };

  // ── enter edit mode ─────────────────────────────────────────────────────────

  const handleEditClick = () => {
    setSaveError('');
    // Re-seed from current venue snapshot in case venue was refreshed
    setEditPhone(venue?.phone || '');
    setEditWebsite(venue?.website || '');
    setEditAddress(venue?.address || venue?.street || '');
    setEditLandmarkHint(venue?.landmarkHint || '');
    setEditMode(true);
  };

  // ── cancel edit ─────────────────────────────────────────────────────────────

  const handleCancel = () => {
    setSaveError('');
    setEditMode(false);
  };

  // ── save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaveError('');
    setSaving(true);

    const token = getAuthToken();
    if (!token) {
      setSaveError(t('vd.notLoggedInRefreshAndLogIn'));
      setSaving(false);
      return;
    }

    // Build payload — only include changed, non-empty fields
    const body = {};
    const trimPhone = editPhone.trim();
    const trimWebsite = editWebsite.trim();
    const trimAddress = editAddress.trim();

    const trimLandmark = editLandmarkHint.trim();
    if (trimPhone !== (venue?.phone || '').trim()) body.phone = trimPhone;
    if (trimWebsite !== (venue?.website || '').trim()) body.website = trimWebsite;
    if (trimAddress !== (venue?.address || venue?.street || '').trim()) body.address = trimAddress;
    if (trimLandmark !== (venue?.landmarkHint || '').trim()) body.landmarkHint = trimLandmark;

    // If nothing changed, just exit edit mode silently
    if (Object.keys(body).length === 0) {
      setSaving(false);
      setEditMode(false);
      return;
    }

    try {
      const base = getApiBaseUrl();
      const venueId = venue?.id || venue?._id;
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(venueId)}/info`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (res.status === 401) {
        setSaveError(t('vd.sessionExpiredLogInAgain'));
        setSaving(false);
        return;
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || t('vd.serverErrorWithCode', { code: res.status }));
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || t('vd.saveFailedTryAgain'));
      }

      // Optimistically update local venue snapshot
      setVenue(prev => ({
        ...prev,
        phone: 'phone' in body ? body.phone : prev?.phone,
        website: 'website' in body ? body.website : prev?.website,
        address: 'address' in body ? body.address : prev?.address,
      }));

      setSaving(false);
      setEditMode(false);
      showToast('✅ ' + t('vd.saved'));
    } catch (err) {
      console.error('[VenueDashboardInfoPage] save error:', err);
      setSaveError(err.message || t('vd.somethingWentWrongTryAgain'));
      setSaving(false);
    }
  };

  const canEdit = myRole === 'owner' || myRole === 'manager';

  // ── render: loading ─────────────────────────────────────────────────────────

  if (pageState === 'loading') {
    return (
      <div style={styles.screen}>
        <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate(-1)} aria-label={t('vd.back')}>←</button>
          <span style={styles.headerTitle}>{t('vd.infoVitals')}</span>
          <div style={{ width: 40 }} />
        </div>
        <div style={styles.centreBlock}>
          <Spinner />
          <p style={styles.centreText}>{t('vd.loading')}</p>
        </div>
      </div>
    );
  }

  // ── render: auth error ──────────────────────────────────────────────────────

  if (pageState === 'error-auth') {
    return (
      <div style={styles.screen}>
        <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate(-1)} aria-label={t('vd.back')}>←</button>
          <span style={styles.headerTitle}>{t('vd.infoVitals')}</span>
          <div style={{ width: 40 }} />
        </div>
        <div style={styles.centreBlock}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>🔒</div>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
            {t('vd.pleaseLogIn')}
          </p>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px', maxWidth: 260, lineHeight: 1.5, textAlign: 'center' }}>
            {t('vd.needLoggedInToViewPage')}
          </p>
          <button
            style={styles.primaryBtn}
            onClick={() => navigate('/login')}
          >
            {t('vd.logIn')}
          </button>
        </div>
      </div>
    );
  }

  // ── render: generic error ───────────────────────────────────────────────────

  if (pageState === 'error-generic') {
    return (
      <div style={styles.screen}>
        <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => navigate(-1)} aria-label={t('vd.back')}>←</button>
          <span style={styles.headerTitle}>{t('vd.infoVitals')}</span>
          <div style={{ width: 40 }} />
        </div>
        <div style={styles.centreBlock}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>⚠️</div>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
            {t('vd.somethingWentWrong')}
          </p>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px', maxWidth: 260, lineHeight: 1.5, textAlign: 'center' }}>
            {t('vd.couldNotLoadVenueInfo')}
          </p>
          <button style={styles.primaryBtn} onClick={fetchVenueData}>
            {t('vd.retry')}
          </button>
        </div>
      </div>
    );
  }

  // ── render: ready ───────────────────────────────────────────────────────────

  return (
    <div style={styles.screen}>
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>

      {/* Toast */}
      <Toast message={toastMsg} type="success" visible={toastVisible} />

      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(-1)} aria-label={t('vd.back')}>←</button>
        <span style={styles.headerTitle}>{t('vd.infoVitals')}</span>
        <div style={{ width: 40 }} />
      </div>

      {/* Body */}
      <div style={styles.body}>

        {/* Venue name strip */}
        <div style={styles.venueNameStrip}>
          <span style={styles.venueName}>{venue?.name || t('vd.venueFallback')}</span>
        </div>

        {/* Card */}
        <div style={styles.card}>

          {editMode ? (
            /* ── EDIT MODE ─────────────────────────────────────────────────── */
            <>
              <TextInput
                label={t('vd.phone')}
                type="tel"
                value={editPhone}
                onChange={setEditPhone}
                placeholder={t('vd.phonePlaceholder')}
              />
              <TextInput
                label={t('vd.website')}
                type="url"
                value={editWebsite}
                onChange={setEditWebsite}
                placeholder={t('vd.websitePlaceholder')}
              />
              <TextInput
                label={t('vd.streetAddress')}
                value={editAddress}
                onChange={setEditAddress}
                placeholder={t('vd.addressPlaceholder')}
              />
              <TextInput
                label={t('vd.landmarkDirections')}
                value={editLandmarkHint}
                onChange={setEditLandmarkHint}
                placeholder={t('vd.landmarkPlaceholder')}
              />

              {/* Inline error */}
              {saveError ? (
                <div style={styles.errorBanner}>
                  {saveError}
                </div>
              ) : null}

              {/* Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                <button
                  style={{
                    ...styles.primaryBtn,
                    opacity: saving ? 0.75 : 1,
                    cursor: saving ? 'default' : 'pointer',
                  }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving && <ButtonSpinner />}
                  {saving ? t('vd.saving') : t('vd.saveChanges')}
                </button>
                <button
                  style={styles.outlineBtn}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  {t('vd.cancel')}
                </button>
              </div>
            </>
          ) : (
            /* ── VIEW MODE ──────────────────────────────────────────────────── */
            <>
              <InfoRow label={t('vd.phone')} value={venue?.phone} />
              <InfoRow label={t('vd.website')} value={venue?.website} />
              <InfoRow label={t('vd.streetAddress')} value={venue?.address || venue?.street} />
              <InfoRow label={t('vd.city')} value={venue?.city} />
              <InfoRow label={t('vd.district')} value={venue?.district} />
              <InfoRow label={t('vd.provinceState')} value={venue?.province} />
              <InfoRow label={t('vd.country')} value={venue?.country} />
              <InfoRow label={t('vd.postalCode')} value={venue?.postalCode} />
              <InfoRow label={t('vd.landmarkDirections')} value={venue?.landmarkHint} />

              {/* Edit button — owners and managers only */}
              {canEdit && (
                <div style={{ marginTop: 24 }}>
                  <button
                    style={styles.primaryBtn}
                    onClick={handleEditClick}
                  >
                    {t('vd.edit')}
                  </button>
                </div>
              )}
            </>
          )}

        </div>

        {/* Bottom breathing room */}
        <div style={{ height: 40 }} />
      </div>
      <DashboardNavBar />
    </div>
  );
};

// ─── style objects ────────────────────────────────────────────────────────────

const styles = {
  screen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f9fafb',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  header: {
    height: 64,
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },

  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: 22,
    fontWeight: 700,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 8,
    lineHeight: 1,
    width: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  headerTitle: {
    color: 'white',
    fontWeight: 700,
    fontSize: 16,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    padding: '0 8px',
  },

  body: {
    flex: 1,
    padding: '16px 16px 0',
    maxWidth: 560,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },

  venueNameStrip: {
    marginBottom: 12,
  },

  venueName: {
    fontSize: 14,
    color: '#9ca3af',
    fontWeight: 500,
  },

  card: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '4px 20px 20px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
  },

  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '0 24px',
    height: 48,
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
    boxSizing: 'border-box',
  },

  outlineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 48,
    padding: '0 24px',
    background: 'white',
    color: '#374151',
    border: '1.5px solid #e5e7eb',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxSizing: 'border-box',
  },

  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 10,
    color: '#b91c1c',
    fontSize: 13,
    fontWeight: 500,
    padding: '10px 14px',
    marginBottom: 12,
    lineHeight: 1.4,
  },

  centreBlock: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 8,
    textAlign: 'center',
  },

  centreText: {
    fontSize: 14,
    color: '#9ca3af',
    margin: 0,
    marginTop: 8,
  },
};

export default VenueDashboardInfoPage;
